import React, { FC, useMemo } from "react";
import { KbArticleVariant } from "enums/KbArticle";
import { sanitizeArticleHtml } from "utils/sanitizeHtml";
import { KbArticleRoot } from "./KbArticle.styles";
import "./KbArticle.css";

export interface KbArticleProps {
  // Raw KB HTML exactly as the backend stores it. Sanitized here; callers
  // never pre-sanitize.
  html?: string | null;
  variant?: KbArticleVariant;
  // Merged onto the root so `styled(KbArticle)` shells work.
  className?: string;
}

// Knowledge-base HTML rendered with the shared `.sabo-article` stylesheet.
// Owns the render contract: sanitizing, the single root class, the density
// variant, and the only import of the stylesheet. It takes no children on
// purpose — the stylesheet's `.sabo-article *` reset must only ever touch KB
// HTML, so chrome wraps around this component, never inside it.
const KbArticle: FC<KbArticleProps> = ({
  html,
  variant = KbArticleVariant.Document,
  className,
}) => {
  const safeHtml = useMemo(
    () => sanitizeArticleHtml(html ?? "").trim(),
    [html],
  );
  if (!safeHtml) return null;

  const rootClassName = [
    "sabo-article",
    variant === KbArticleVariant.Embedded && "sabo-article--embedded",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <KbArticleRoot
      className={rootClassName}
      dangerouslySetInnerHTML={{ __html: safeHtml }}
    />
  );
};

export default KbArticle;
